import {FormEvent, useState} from "react";
import {Alert, Card, Form} from "react-bootstrap";
import {Link, useSearchParams} from "react-router";
import {UsersApi} from "../api/UsersApi";
import {ErrorAlert} from "../components/ErrorAlert";
import {SubmitButton} from "../components/SubmitButton";
import {MIN_PASSWORD_LENGTH, newPasswordProblem} from "../components/format";
import {Field, FieldControl} from "../components/Field";
import {usePageTitle} from "../components/usePageTitle";
import {auth, format, rich, validation} from "../i18n";

// Opened from the "forgot password" email: /reset-password?token=…, or with &new=1 from the email a new account gets when
// a guest buys (the API also confirms its address then).
export function ResetPasswordPage() {
    const [params] = useSearchParams();
    const token = params.get('token') ?? '';
    const isNew = params.get('new') === '1';
    const text = isNew ? { ...auth.resetPassword, ...auth.setPassword } : auth.resetPassword;
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<unknown>();
    const problem = newPasswordProblem(password, confirm);
    usePageTitle(text.title);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (problem) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            await UsersApi.resetForgottenPassword(token, password);
            setDone(true);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="auth-card mx-auto mt-md-4">
            <Card.Body className="p-4">
                <h1 className="h4 mb-3">{text.title}</h1>
                {isNew && token && !done && <p className="text-body-secondary">{auth.setPassword.intro}</p>}
                {!token ? (
                    <Alert variant="warning">{rich(auth.resetPassword.noToken, { link: text => <Link to="/forgot-password">{text}</Link> })}</Alert>
                ) : done ? (
                    <Alert variant="success">{rich(text.done, { link: x => <Link to="/login">{x}</Link> })}</Alert>
                ) : (
                    <>
                        <ErrorAlert error={error} messages={{ 404: text.expired }}/>
                        <Form noValidate onSubmit={submit}>
                            <Field className="mb-3" controlId="new-password" label={auth.resetPassword.newPassword} help={format(validation.passwordHelp, { min: MIN_PASSWORD_LENGTH })}
                                   error={validated && password.length < MIN_PASSWORD_LENGTH && validation.passwordShort}>
                                <FieldControl type="password" autoComplete="new-password" autoFocus value={password} onChange={e => setPassword(e.target.value)}/>
                            </Field>
                            <Field className="mb-4" controlId="confirm-password" label={auth.resetPassword.confirmPassword} error={validated && problem}>
                                <FieldControl type="password" autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)}/>
                            </Field>
                            <SubmitButton busy={busy} className="w-100">{text.submit}</SubmitButton>
                        </Form>
                        <div className="text-center mt-3">
                            <Link to="/forgot-password" className="small">{auth.resetPassword.requestNewLink}</Link>
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    );
}
